/**
 * Every word the phone app says. Plain verbs, sentence case, no jargon and no filler.
 * A label names the thing you control; a button says exactly what happens when you press it.
 */
export const M = {
  appName: "CherryClipz",
  tagline: "Cherrypick your clipz.",
  homeSub: "The fastest way to pull the good bits out of a video — then line them up into a cut you can share.",
  homeLines: [
    "Pre-cut by time, or by what people say",
    "Cherrypick the moments, in any order",
    "Stitch them into one video and share it",
  ],
  pickVideos: "Pick videos",
  openLibrary: "Open from my library",
  libraryTitle: "Your library",
  libraryEmpty: "Nothing here yet. Send a video to CherryClipz and it will appear.",
  libraryLoading: "Reading your library…",
  libraryOpening: "Opening…",
  libraryClips: (n) => `${n} clip${n === 1 ? "" : "s"}`,
  libraryFailed: "Could not read your library.",
  libraryNeedsSignIn: "Sign in to see the videos you have sent to CherryClipz.",
  libraryStreamNote: "This video plays from CherryClipz. Sharing it from the phone is coming with captions.",
  libraryLinkExpired: "Sign in to reopen this library video.",
  audioOnly: "Audio",
  findWords: "By what's said",
  findWordsSub: "One clip per sentence",
  chooseHow: "Choose how to cut by what's said",
  choiceFree: "Free",
  choiceFreeLines: ["Your phone listens", "Private · works offline", "No account needed"],
  chooseFree: "Choose this",
  chooseFreeFull: "Choose Free — your phone listens",
  choiceCloud: "CherryClipz AI",
  choiceCloudLines: ["Far more accurate", "Who is speaking · emotions", "A shareable video spreadsheet"],
  chooseCloud: "Choose this",
  chooseCloudFull: "Choose CherryClipz AI",
  onPhoneOff: "Only in the installed app",
  withCloudNote: "Included in your CherryClipz plan. Free accounts get one hour to start.",
  planLimit: "Your free hour is used up.",
  planUpgrade: "See plans on CherryClipz",
  feedback: "Send feedback",
  feedbackSub: "Tell Isaac what worked and what did not.",
  lastProject: "Last project",
  projects: "Projects",
  seeAll: "See all",
  libOnPhone: "On this phone",
  libSent: "Sent to CherryClipz",
  howTime: "cut by time",
  howPhone: "phone transcript",
  howCloud: "CherryClipz AI",
  recent: "Recent",
  recentOpen: "Open",
  recentForget: "Forget",
  recentWhen: (ms) => {
    const d = Math.max(0, Date.now() - ms);
    if (d < 60e3) return "just now";
    if (d < 3600e3) return `${Math.round(d / 60e3)} min ago`;
    if (d < 86400e3) return `${Math.round(d / 3600e3)} h ago`;
    return `${Math.round(d / 86400e3)} d ago`;
  },
  recentPicks: (n) => (n === 1 ? "1 kept" : `${n} kept`),
  untrim: "Put it back",
  trimmedTo: "trimmed",
  pickMore: "Pick different videos",
  backToClips: "Back to your clips",
  reading: "Reading your videos…",
  readFailed: (name) => `Couldn't read ${name}. Try a different video.`,
  readNone: "None of those could be read as video.",
  back: "Back",
  done: "Done",
  whatsInHere: "What's in here",

  // choosing how several videos go together
  videosPicked: (n) => `${n} videos`,
  modeSeparate: "Keep them apart",
  modeSeparateSub: "Each video keeps its own clock.",
  modeStitch: "Join them up",
  modeStitchSub: "One long video, in this order.",

  // how to cut
  cutTitle: "Pre-cut it",
  cutSub: "By time, or by what people say. You can trim any clip after.",
  bySentence: "Sentences",
  bySentenceSub: "Your phone reads the video",
  bySentenceOff: "Only in the app",
  reCut: "Re-cut",
  seconds: (n) => `${n}s`,
  clipsAbout: (n) => (n === 1 ? "1 clip" : `${n} clips`),
  findWords: "Find the words",
  findWordsSub: "Cut where people speak. On the phone, no internet.",
  findWordsOff: "Only in the installed app.",
  listening: "Reading your video",
  listeningOne: (i, n) => (n > 1 ? `Video ${i} of ${n}` : "Finding the words"),
  listeningPct: (p) => `${p}%`,
  listeningNote: "Your phone is doing this. Nothing is uploaded.",
  heardNothing: "No speech found. You can still cut by time.",
  transcribeFailed: "Couldn't listen to that video.",
  cancel: "Cancel",
  searchPlaceholder: "Search what was said",
  noMatches: (q) => `Nothing said about "${q}".`,
  clearSearch: "Clear",
  revealInContext: "Show this clip in context",
  spokenTitle: (n) => (n === 1 ? "1 thing said" : `${n} things said`),

  // clips
  clipsTitle: (chop, n) => `${chop}s · ${n} clips`,
  makingPosters: (done, total) => `Getting pictures… ${done} of ${total}`,
  picked: (n) => (n === 1 ? "1 clip" : `${n} clips`),
  clipN: (n) => `Clip ${n}`,
  playEdit: "Play",
  stop: "Stop",
  share: "Share",
  making: "Making video…",
  makingPct: (p) => `Making video… ${p}%`,
  shareNotHere: "Sharing only works in the installed app.",
  shareFailed: "Couldn't make the video. Try fewer clips.",
  tapToWatch: "Tap a clip to watch it. Tap the circle to keep it.",
  playFailed: "This video won't play on this phone.",

  // trim
  removeClip: "Remove",
  trimStart: "Move the start",
  trimEnd: "Move the end",
  trimHint: "Drag the handles — you can reach past the original cut. Tap inside the red to jump there.",
  hearIt: "Hear it",

  // The CherryClipz AI pass.
  //
  // NO PRICE ON THIS SCREEN. The app never charges anything — paying happens on the website,
  // and the hours come from the account's plan, which the backend already enforces (a refused
  // presign comes back as PLAN_LIMIT and opens the plans page). This screen used to say
  // "roughly $0.02 · about $1 an hour of video", which is a number nobody charges and which
  // flatly contradicted the pop-up one tap earlier ("Included in your CherryClipz plan").
  // Say the length, say where the hours come from, and stop.
  aiTitle: "CherryClipz AI",
  aiBlurb: "Send this video to CherryClipz. You get a far more accurate transcript, who is speaking, and how each line feels — so you can filter by person and by mood.",
  aiLength: (clock) => `${clock} of video`,
  aiLengthNote: "This comes out of your plan's hours. Nothing is charged in the app.",
  aiPoints: [
    "Much better words, including names and places",
    "Who is speaking, so you can filter by person",
    "How each line feels, so you can find the strong bits",
  ],
  aiNeedsAccount: "Needs your CherryClipz account and a connection.",
  // which language(s) the speech is in — Auto detects among the three and keeps switches
  langTitle: "Language",
  langAuto: "Auto",
  langEn: "English",
  langHe: "Hebrew",
  langYi: "Yiddish",
  langMixed: "Mixed",
  langNote: "Auto listens for English, Hebrew and Yiddish, and keeps it when a sentence switches.",
  aiSend: "Send to CherryClipz",
  aiSignIn: "Sign in to CherryClipz",
  aiSignedInAs: (who) => `Signed in as ${who}`,
  aiSending: "Sending to CherryClipz",
  aiPhase: {
    reading: "Getting the video ready",
    asking: "Asking CherryClipz",
    sending: "Uploading",
    listening: "CherryClipz is listening",
  },
  aiWaitNote: "This can take a few minutes for a long video. You can leave this screen.",
  aiFailed: "That did not go through.",
  aiSignedOut: "You are signed out of CherryClipz. Sign in and try again.",

  // sign in
  signInTitle: "Sign in",
  signInBlurb: "Use the same account as the CherryClipz website. We will email you a code.",
  emailLabel: "Your email",
  codeLabel: "The code we emailed",
  sendCodeBtn: "Email me a code",
  signInBtn: "Sign in",
  codeSent: (email) => `Code sent to ${email}.`,
  signOut: "Sign out",

  // filters the cloud pass unlocks
  everyone: "Everyone",
  allMoods: "Any mood",
  moods: { positive: "Positive", neutral: "Neutral", negative: "Negative" },
  aiBack: "Not now",

  // looks
  accountTitle: "Your account",
  accountOn: "Signed in to CherryClipz",
  accountOff: "Not signed in",
  checkConnection: "Check my connection",
  diagOk: (who) => (who ? `All good. Signed in as ${who}.` : "All good. You are signed in."),
  diagOffline: "Can't reach CherryClipz. Check your wifi or mobile data.",
  diagSignedOut: "CherryClipz is reachable. You are not signed in.",
  diagRejected: "Please sign in again.",
  diagDetails: "Show details",
  diagHideDetails: "Hide details",
  diagBackend: "CherryClipz:",
  diagToken: "Sign-in saved:",
  diagAccepted: "Accepted:",
  diagLast: "Last sign-in:",
  diagServer: "Server build:",
  diagApp: "This app:",
  // a newer build exists — shown only when this build's number AND the server's are both known
  updateTitle: "A newer CherryClipz is out",
  updateSub: (name) => (name ? `Version ${name} is ready. Tap to get it.` : "Tap to get it."),
  looksTitle: "How it looks",
  lookCards: "Normal",
  lookCardsSub: "Picture and words",
  lookFeed: "Big",
  lookFeedSub: "Large pictures",
  lookList: "Small",
  lookListSub: "Fits more on screen",

  // trim steppers
  nudgeStart: "Start",
  nudgeEnd: "End",

  // settings
  // silences
  showSilences: "Show the gaps",
  hideSilences: "Hide the gaps",
  silence: "silence",

  // supercut
  keepAll: (n) => `Keep all ${n}`,
  keepAllDone: (n) => `Kept ${n}`,

  settingsTitle: "What's in here",
  version: (v, build) => (build ? `Version ${v} · build ${build}` : `Version ${v}`),
};
